/**
 * `P0-UIK-009`: dates and times, section 06, 6.5.3.
 *
 * **Every instant is stored and transported in UTC; the display timezone is
 * `Africa/Bamako`, applied at the EDGE and never assumed.** Bamako is UTC+0
 * all year with no daylight saving, which is exactly why this is dangerous:
 * every wrong implementation looks right from Bamako, and only breaks for a
 * reader elsewhere , a founder in Paris in July, an auditor in Dakar. So the
 * timezone is named on every call rather than left to the host.
 *
 * **`now` is a PARAMETER, never read here.** 9.2.4's `D2` bans ambient time
 * outside `platform/src/clock`, and a relative formatter that reads the clock
 * itself cannot be tested without waiting for tomorrow.
 */

export const BAMAKO = "Africa/Bamako";

/** 6.5.3 wants French month names in lower case; `Intl` capitalises some locales. */
const lower = (text: string): string => text.toLocaleLowerCase("fr-FR");

const parts = (instant: Date, options: Intl.DateTimeFormatOptions): Map<string, string> =>
  new Map(
    new Intl.DateTimeFormat("fr-FR", { timeZone: BAMAKO, ...options })
      .formatToParts(instant)
      .map((part) => [part.type, part.value]),
  );

/** `1er`, never `1`, for the first of the month (6.5.3). Every other day is bare. */
const dayOfMonth = (day: string): string => (day === "1" ? "1er" : day);

/** `18 août 2026`. A `date` in prose. */
export function formatDate(instant: Date): string {
  const p = parts(instant, { day: "numeric", month: "long", year: "numeric" });
  return `${dayOfMonth(p.get("day") ?? "")} ${lower(p.get("month") ?? "")} ${p.get("year") ?? ""}`;
}

/** `22 octobre`, `1er septembre`: the day and the month in words, no year (additive; D2 draws the recording day so). */
export function formatDayMonth(instant: Date): string {
  const p = parts(instant, { day: "numeric", month: "long" });
  return `${dayOfMonth(p.get("day") ?? "")} ${lower(p.get("month") ?? "")}`;
}

/** `août`, from the wire's `2026-08` (the month a served pièces reading found or missed): the month's name alone, lower case, Bamako's calendar. */
export function formatMonthName(yearMonth: string): string {
  const [year, month] = yearMonth.split("-").map(Number);
  if (year === undefined || month === undefined || !Number.isInteger(year) || !Number.isInteger(month))
    return "";
  return lower(parts(new Date(Date.UTC(year, month - 1, 15)), { month: "long" }).get("month") ?? "");
}

/** `8 août`, `26 juil.` , the abbreviated form, for a dense column. */
export function formatShortDate(instant: Date): string {
  const p = parts(instant, { day: "numeric", month: "short" });
  return `${dayOfMonth(p.get("day") ?? "")} ${lower(p.get("month") ?? "")}`;
}

/** `8 août, 12:40`, on 24 hours. */
export function formatDateTime(instant: Date): string {
  const p = parts(instant, {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${dayOfMonth(p.get("day") ?? "")} ${lower(p.get("month") ?? "")}, ${p.get("hour") ?? ""}:${p.get("minute") ?? ""}`;
}

/** `08:41`, the hour and the minute alone, on 24 hours, in Bamako: a time on a line that already says the day (the bay's trace). */
export function formatClock(instant: Date): string {
  const p = parts(instant, { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${p.get("hour") ?? ""}:${p.get("minute") ?? ""}`;
}

/** `25 octobre 2026, 10:00`, the date with its year and its time on 24 hours (`ODF-3`; additive, `formatDateTime`'s words are untouched). */
export function formatDateAndTime(instant: Date): string {
  const p = parts(instant, {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${dayOfMonth(p.get("day") ?? "")} ${lower(p.get("month") ?? "")} ${p.get("year") ?? ""}, ${p.get("hour") ?? ""}:${p.get("minute") ?? ""}`;
}

/** `jeudi 13 août 2026` , the full weekday, lower case in prose. */
export function formatWeekday(instant: Date): string {
  const p = parts(instant, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  return `${lower(p.get("weekday") ?? "")} ${dayOfMonth(p.get("day") ?? "")} ${lower(p.get("month") ?? "")} ${p.get("year") ?? ""}`;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * 6.5.3's relative ladder: `à l'instant`, `il y a N minutes`, `il y a N
 * heures`, `hier`, then the short date. **Never a relative form beyond 7
 * days** , "il y a 34 jours" is a sentence a reader has to do arithmetic on,
 * and the date they actually want is shorter to read.
 */
export function formatRelative(instant: Date, now: Date): string {
  // `Math.trunc`, not `Math.floor`: `hakili/no-money-float` bans floor outside
  // `platform/money` and `simulation/domain`, and it caught this. The two are
  // identical here , every branch below is reached only when `elapsed` is
  // positive , so the rule cost nothing and the ban stays whole. `backoff.ts`
  // records the same substitution for the same reason.
  const elapsed = now.getTime() - instant.getTime();
  if (elapsed < MINUTE) return "à l'instant";
  if (elapsed < HOUR) {
    const minutes = Math.trunc(elapsed / MINUTE);
    return `il y a ${String(minutes)} minute${minutes > 1 ? "s" : ""}`;
  }
  if (elapsed < DAY) {
    const hours = Math.trunc(elapsed / HOUR);
    return `il y a ${String(hours)} heure${hours > 1 ? "s" : ""}`;
  }
  const days = Math.trunc(elapsed / DAY);
  if (days === 1) return "hier";
  if (days <= 7) return `il y a ${String(days)} jours`;
  return formatShortDate(instant);
}

/** `7 jours`, `1 jour` , the singular is not a rounding of the plural. */
export const formatDuration = (days: number): string =>
  `${String(days)} jour${Math.abs(days) > 1 ? "s" : ""}`;

/** ISO 8601 UTC with `Z`: the wire format of 6.5.3, never a display form. */
export const toWire = (instant: Date): string => instant.toISOString();

/** `YYYY-MM-DD` in Bamako , a calendar date, which 6.5.3 keeps a date. */
export function toWireDate(instant: Date): string {
  const p = parts(instant, { year: "numeric", month: "2-digit", day: "2-digit" });
  return `${p.get("year") ?? ""}-${p.get("month") ?? ""}-${p.get("day") ?? ""}`;
}
